import React, { useEffect, useState, useRef } from 'react';
import AdminBar from '../components/AdminBar';
import Copyright from '../components/Copyright';
import { useThemeMode } from '../components/ThemeModeContext';
import { useNavigate } from 'react-router-dom';
import { createNewAppRequest, setCurrentAppContext } from '../components/appCreationUtils';
import axios from 'axios';
import { serverDomain } from './config';

const BLANK_DOC = '<!doctype html><html><head><meta charset="utf-8"/></head><body></body></html>';
const NO_CONTENT_DOC = '<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><style>html,body{height:100%;margin:0}body{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#111;color:#fff;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:24px;text-align:center;padding:16px;box-sizing:border-box;}</style></head><body><h1>This app has<br>no content yet.</h1></body></html>';
const HOVER_PREVIEW_DELAY_MS = 3000;
const MOBILE_CONTEXT_MENU_DELAY_MS = 500;
const HOVER_PREVIEW_WIDTH = 340;
const HOVER_PREVIEW_HEIGHT = 287;
const HOVER_PREVIEW_HEADER_HEIGHT = 32;
const HOVER_PREVIEW_CLOSE_DELAY_MS = 140;
const HOVER_PREVIEW_VIEWPORT_WIDTH = 1024;
const HOVER_PREVIEW_VIEWPORT_HEIGHT = 768;
const HOVER_PREVIEW_SCALE = HOVER_PREVIEW_WIDTH / HOVER_PREVIEW_VIEWPORT_WIDTH;

export default function Home() {
  const { isDarkMode } = useThemeMode();
  const headerBg = isDarkMode ? '#000000' : '#003366';
  const headerText = '#ffffff';
  const pageBg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const pageText = isDarkMode ? '#f5f5f5' : '#000000';
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checked, setChecked] = useState(false);
  const [toast, setToast] = useState('');
  const [allApps, setAllApps] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState({ open: false, x: 0, y: 0, app: null });
  const contextMenuRef = useRef(null);
  const renameInputRef = useRef(null);
  const [renameDialog, setRenameDialog] = useState({ open: false, name: '', app: null, loading: false });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, app: null, loading: false });
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isDesktopPreviewEnabled, setIsDesktopPreviewEnabled] = useState(false);
  const [hoverPreview, setHoverPreview] = useState({
    open: false,
    appId: null,
    appName: '',
    htmlSource: '',
    isLoading: false,
    left: 0,
    top: 0
  });
  const suppressNextClickRef = useRef(false);
  const isTouchingRef = useRef(false);
  const hoverPreviewTimerRef = useRef(null);
  const hoverPreviewCloseTimerRef = useRef(null);
  const mobilePreviewAnchorRectRef = useRef(null);
  const hoverPreviewRequestRef = useRef(0);
  const hoveredAppIdRef = useRef(null);
  const previewHoveredRef = useRef(false);

  const clearHoverPreviewTimer = () => {
    if (hoverPreviewTimerRef.current) {
      clearTimeout(hoverPreviewTimerRef.current);
      hoverPreviewTimerRef.current = null;
    }
  };

  const clearHoverPreviewCloseTimer = () => {
    if (hoverPreviewCloseTimerRef.current) {
      clearTimeout(hoverPreviewCloseTimerRef.current);
      hoverPreviewCloseTimerRef.current = null;
    }
  };

  const hideHoverPreview = () => {
    clearHoverPreviewTimer();
    clearHoverPreviewCloseTimer();
    hoveredAppIdRef.current = null;
    previewHoveredRef.current = false;
    hoverPreviewRequestRef.current += 1;
    setHoverPreview((current) => (
      current.open || current.appId || current.isLoading || current.htmlSource
        ? {
            open: false,
            appId: null,
            appName: '',
            htmlSource: '',
            isLoading: false,
            left: 0,
            top: 0
          }
        : current
    ));
  };

  const getHoverPreviewPosition = (rect) => {
    const margin = 16;
    const gap = 18;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const placeLeft = rect.left >= viewportWidth / 2;
    const shouldShowBelow = rect.top < (HOVER_PREVIEW_HEIGHT + margin + 24);
    let left = placeLeft
      ? rect.left - HOVER_PREVIEW_WIDTH - gap
      : rect.right + gap;
    let top = shouldShowBelow
      ? rect.bottom + 14
      : rect.top - HOVER_PREVIEW_HEIGHT - 14;

    left = Math.max(margin, Math.min(left, viewportWidth - HOVER_PREVIEW_WIDTH - margin));
    top = Math.max(margin, Math.min(top, viewportHeight - HOVER_PREVIEW_HEIGHT - margin));

    return { left, top };
  };

  const scheduleHoverPreviewHide = () => {
    clearHoverPreviewTimer();
    clearHoverPreviewCloseTimer();
    hoverPreviewCloseTimerRef.current = setTimeout(() => {
      if (!hoveredAppIdRef.current && !previewHoveredRef.current) {
        hideHoverPreview();
      }
    }, HOVER_PREVIEW_CLOSE_DELAY_MS);
  };

  const getFallbackPreviewRect = () => {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const width = 80;
    const height = 80;
    const left = Math.max(16, (viewportWidth - width) / 2);
    const top = Math.max(16, (viewportHeight - height) / 2);

    return {
      left,
      top,
      right: left + width,
      bottom: top + height
    };
  };

  const loadHoverPreview = (app, rect, options = {}) => {
    if (!app?.app_id) return;
    const { allowMobile = false } = options;
    if (!allowMobile && !isDesktopPreviewEnabled) return;

    const token = localStorage.getItem('appstar_jwt');
    if (!token) return;

    hoveredAppIdRef.current = app.app_id;
    const requestId = hoverPreviewRequestRef.current + 1;
    hoverPreviewRequestRef.current = requestId;

    setHoverPreview({
      open: true,
      appId: app.app_id,
      appName: app.app_name || 'App Preview',
      htmlSource: '',
      isLoading: true,
      ...getHoverPreviewPosition(rect)
    });

    axios
      .get(serverDomain + 'get-sources.php', { params: { app_id: app.app_id, token } })
      .then((res) => {
        if (hoverPreviewRequestRef.current !== requestId || hoveredAppIdRef.current !== app.app_id) return;
        let html = typeof res?.data?.data?.htmlSource === 'string' ? res.data.data.htmlSource : '';
        html = String(html).replace(/\r\n/g, '\n');
        setHoverPreview((current) => (
          current.appId === app.app_id
            ? { ...current, htmlSource: html || '', isLoading: false }
            : current
        ));
      })
      .catch((err) => {
        console.error('Error loading hover preview:', err);
        if (hoverPreviewRequestRef.current !== requestId || hoveredAppIdRef.current !== app.app_id) return;
        setHoverPreview((current) => (
          current.appId === app.app_id
            ? { ...current, htmlSource: '', isLoading: false }
            : current
        ));
      });
  };

  const startHoverPreviewTimer = (app, rect) => {
    if (!isDesktopPreviewEnabled || contextMenu.open || renameDialog.open || deleteDialog.open) return;
    clearHoverPreviewTimer();
    clearHoverPreviewCloseTimer();
    previewHoveredRef.current = false;
    hoveredAppIdRef.current = app.app_id;
    hoverPreviewTimerRef.current = setTimeout(() => {
      if (hoveredAppIdRef.current === app.app_id) {
        loadHoverPreview(app, rect);
      }
    }, HOVER_PREVIEW_DELAY_MS);
  };

  // Ensure context menu stays within viewport (clamp to left/top if overflowing)
  useEffect(() => {
    if (!contextMenu.open) return;
    // Measure after render
    const adjustPosition = () => {
      const el = contextMenuRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const margin = 8; // small viewport margin
      let newX = contextMenu.x;
      let newY = contextMenu.y;

      // If menu would overflow right, move it left
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      if (rect.right > viewportWidth - margin) {
        newX -= (rect.right - (viewportWidth - margin));
      }
      // If menu would overflow bottom, move it up
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.bottom > viewportHeight - margin) {
        newY -= (rect.bottom - (viewportHeight - margin));
      }
      // If menu would overflow left, nudge right
      if (rect.left < margin) {
        newX += (margin - rect.left);
      }
      // If menu would overflow top, nudge down
      if (rect.top < margin) {
        newY += (margin - rect.top);
      }

      if (newX !== contextMenu.x || newY !== contextMenu.y) {
        setContextMenu((s) => ({ ...s, x: newX, y: newY }));
      }
    };
    // Use rAF to run after paint for accurate rect
    const id = requestAnimationFrame(adjustPosition);
    return () => cancelAnimationFrame(id);
  }, [contextMenu.open, contextMenu.x, contextMenu.y]);

  useEffect(() => {
    const token = localStorage.getItem('appstar_jwt');
    console.log('appstar_jwt token:', token);
    if (!token) {
      navigate('/start');
      return;
    }
    axios
      .post(serverDomain + 'is_user_logged_in.php', null, { params: { token } })
      .then((response) => {
        // Expect response.data.success to be 0 or 1
        if (response.data && response.data.success === 1) {
          console.log('User is logged in');
          setIsLoggedIn(true);
          // Fetch all apps for this user
          axios
            .post(serverDomain + 'get-all-apps.php', null, { params: { token } })
            .then((appsResponse) => {
              if (appsResponse.data && appsResponse.data.success === 1) {
                setAllApps(appsResponse.data.data.apps || []);
                console.log('Fetched apps:', appsResponse.data.data.apps);
                console.log('All apps response:', appsResponse.data);
              }
            })
            .catch((err) => {
              console.error('Error fetching apps:', err);
            });
        } else {
          console.log('User is not logged in');
          setIsLoggedIn(false);
          localStorage.setItem('appstar_jwt', '');
          localStorage.setItem('appstar_admin', 'false');
          navigate('/login');
        }
      })
      .catch(() => {
        localStorage.setItem('appstar_admin', 'false');
        navigate('/login');
      })
      .finally(() => setChecked(true));
  }, [navigate]);

  useEffect(() => {
    const checkDesktopPreview = () => {
      try {
        const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        setIsDesktopPreviewEnabled(canHover);
      } catch (_) {
        setIsDesktopPreviewEnabled(false);
      }
    };

    checkDesktopPreview();
    window.addEventListener('resize', checkDesktopPreview);
    return () => window.removeEventListener('resize', checkDesktopPreview);
  }, []);

  useEffect(() => {
    if (isDesktopPreviewEnabled) return;
    hideHoverPreview();
  }, [isDesktopPreviewEnabled]);

  useEffect(() => {
    if (!hoverPreview.open) return;
    const handleViewportChange = () => hideHoverPreview();
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [hoverPreview.open]);

  useEffect(() => {
    if (!contextMenu.open && !renameDialog.open && !deleteDialog.open) return;
    hideHoverPreview();
  }, [contextMenu.open, renameDialog.open, deleteDialog.open]);

  useEffect(() => () => {
    clearHoverPreviewTimer();
    clearHoverPreviewCloseTimer();
    hoverPreviewRequestRef.current += 1;
  }, []);

  const refreshAllAppNames = () => {
    const token = localStorage.getItem('appstar_jwt');
    if (!token) return;
    axios
      .post(serverDomain + 'get-all-apps.php', null, { params: { token } })
      .then((appsResponse) => {
        if (appsResponse.data && appsResponse.data.success === 1) {
          setAllApps(appsResponse.data.data.apps || []);
        }
      })
      .catch((err) => {
        console.error('Error fetching app names:', err);
      });
  };

  // Close context menu on outside click or contextmenu if target is outside
  useEffect(() => {
    const handleGlobalMouseDown = (e) => {
      if (!contextMenu.open) return;
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) return;
      // Close menu and suppress the next click to avoid immediate navigation
      suppressNextClickRef.current = true;
      setContextMenu({ open: false, x: 0, y: 0, app: null });
      // Auto-clear after this interaction so the next real tap/click still works.
      setTimeout(() => { suppressNextClickRef.current = false; }, 0);
    };
    const handleGlobalContextMenu = (e) => {
      // On mobile long-press, suppress native context menu and DO NOT close ours
      if (isTouchingRef.current) {
        e.preventDefault();
        return;
      }
      // Desktop/right-click: prevent native menu and close ours if clicking outside
      if (!contextMenu.open) return;
      e.preventDefault();
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) return;
      // Close menu and suppress the next click to avoid immediate navigation
      suppressNextClickRef.current = true;
      setContextMenu({ open: false, x: 0, y: 0, app: null });
      // Auto-clear suppression so the next icon click registers
      setTimeout(() => { suppressNextClickRef.current = false; }, 0);
    };
    if (contextMenu.open) {
      document.addEventListener('mousedown', handleGlobalMouseDown, true);
      document.addEventListener('touchstart', handleGlobalMouseDown, true);
      document.addEventListener('contextmenu', handleGlobalContextMenu, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleGlobalMouseDown, true);
      document.removeEventListener('touchstart', handleGlobalMouseDown, true);
      document.removeEventListener('contextmenu', handleGlobalContextMenu, true);
    };
  }, [contextMenu.open]);

  // When rename dialog opens, focus and select the entire app name text
  useEffect(() => {
    if (!renameDialog.open) return;
    const el = renameInputRef.current;
    if (!el) return;
    // Ensure this runs after dialog paints
    requestAnimationFrame(() => {
      el.focus();
      // Select full text like mouse drag selection
      try {
        el.select();
        el.setSelectionRange(0, el.value.length);
      } catch (e) {
        // Some environments may not support setSelectionRange; ignore
      }
    });
  }, [renameDialog.open]);

  const createNewApp = () => {
    const token = localStorage.getItem('appstar_jwt');
    if (!token) return;

    createNewAppRequest(token)
      .then((response) => {
        console.log('New app created:', response);
        setCurrentAppContext({ appId: response.app_id, appName: response.app_name });
        navigate('/chat');
      })
      .catch((err) => {
        const httpCode = err?.response?.status ?? err?.response?.data?.status ?? 0;
        const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Unknown error';
        console.error('create_new_app create request error:', { httpCode, errorMsg });
        setToast('App could not be created at the moment');
        setTimeout(() => setToast(''), 3000);
      });
  };

  const cloneApp = (appToClone) => {
    const token = localStorage.getItem('appstar_jwt');
    if (!token || !appToClone?.app_id) return;

    const iconStyle = null; // generateIconStyle(); //-- use null to keep same icon style when cloning, since user may want that. New app creation will still get random styles. Or use the function to give clones new icon style.

    axios
      .post(serverDomain + 'create_new_app.php', null, {
        params: { token, action: 'clone', app_id: appToClone.app_id, icon_style: iconStyle }
      })
      .then((response) => {
        if (response.data && response.data.success === 1) {
          console.log('App cloned:', response.data);
          localStorage.setItem('appstar_app_name', response.data.app_name);
          localStorage.setItem('appstar_app_id', response.data.app_id);
          navigate('/chat');
        } else {
          const httpCode = response?.data?.status ?? response?.status ?? 0;
          const errorMsg = response?.data?.error || response?.data?.message || 'Unknown error';
          console.error('create_new_app clone failed:', { httpCode, errorMsg, data: response?.data });
          setToast('App could not be cloned at the moment');
          setTimeout(() => setToast(''), 3000);
        }
      })
      .catch((err) => {
        const httpCode = err?.response?.status ?? 0;
        const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Unknown error';
        console.error('create_new_app clone request error:', { httpCode, errorMsg });
        setToast('App could not be cloned at the moment');
        setTimeout(() => setToast(''), 3000);
      });
  };

  const homeAppSearch = (query) => {
    setSearchQuery((query || '').trim());
  };

  const clearHomeSearch = () => {
    setSearchQuery('');
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const displayApps = normalizedSearch
    ? allApps.filter((app) => (app.app_name || '').toLowerCase().includes(normalizedSearch))
    : allApps;
  const appCount = Array.isArray(allApps) ? allApps.length : 0;
  const searchPlaceholder = `Search ${appCount} app${appCount === 1 ? '' : 's'}`;

  return (
    <div style={{ minHeight: '100vh', background: pageBg, color: pageText, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .dialog-btn {
          transition: transform 0.1s ease, filter 0.15s ease, background-color 0.15s ease, box-shadow 0.2s ease;
        }
        .dialog-btn:hover {
          filter: brightness(1.05);
        }
        .dialog-btn:active {
          transform: scale(0.98);
          filter: brightness(0.95);
        }
        .dialog-btn.primary:hover {
          background-color: ${isDarkMode ? '#1a73e8' : '#004a99'} !important;
          filter: none;
        }
        .dialog-btn.danger:hover {
          background-color: #b71c1c;
        }
        .dialog-btn.neutral:hover {
          background-color: ${isDarkMode ? '#505050' : '#d5d5d5'};
        }
        @keyframes pulseRing {
          0% {
            box-shadow: 0 0 0 4px #9e9e9e, 0 2px 8px rgba(0,0,0,0.15);
          }
          50% {
            box-shadow: 0 0 0 4px #ffffff, 0 2px 8px rgba(0,0,0,0.15);
          }
          100% {
            box-shadow: 0 0 0 4px #9e9e9e, 0 2px 8px rgba(0,0,0,0.15);
          }
        }
      `}</style>
      <AdminBar
        bgColor={headerBg}
        textColor={headerText}
        onCreateNewApp={createNewApp}
        showProfileIcon
        showSearch
        onSearch={homeAppSearch}
        onSearchClear={clearHomeSearch}
        searchPlaceholder={searchPlaceholder}
      />
      <div className="home-content" style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {!checked && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid rgba(0,0,0,0.1)',
              borderTop: '4px solid ' + (isDarkMode ? '#ffffff' : '#003366'),
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
          <>
            <div
              style={{
                display: 'grid',
                gap: '24px',
                justifyContent: 'space-evenly',
                width: '100%',
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                justifyItems: 'center',
                alignItems: 'start'
              }}
            >
              {displayApps.map((app) => {
                let iconStyle = { background: '#ccc', textColor: '#000' };
                try {
                  iconStyle = JSON.parse(app.icon_style || '{}');
                } catch (e) {
                  console.error('Error parsing icon_style:', e);
                }
                const hasIconData = !!app.icon_data;
                
                const displayNameMaxLength = 16;
                const appName = app.app_name || 'Untitled';
                const displayName = appName.length > displayNameMaxLength ? appName.substring(0, displayNameMaxLength) + '...' : appName;
                const firstLetter = appName.charAt(0).toUpperCase();
                const isSelected = (
                  (contextMenu.open && contextMenu.app && contextMenu.app.app_id === app.app_id) ||
                  (renameDialog.open && renameDialog.app && renameDialog.app.app_id === app.app_id)
                );
                return (
                  <div
                    title={appName} 
                    key={app.app_id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '80px',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      clearHoverPreviewCloseTimer();
                      startHoverPreviewTimer(app, e.currentTarget.getBoundingClientRect());
                    }}
                    onMouseLeave={() => {
                      hoveredAppIdRef.current = null;
                      scheduleHoverPreviewHide();
                    }}
                    onMouseDown={(e) => {
                      // Preemptively close popup on mousedown to avoid navigation on click
                      hideHoverPreview();
                      if (contextMenu.open) {
                        e.preventDefault();
                        e.stopPropagation();
                        setContextMenu({ open: false, x: 0, y: 0, app: null });
                        suppressNextClickRef.current = true;
                      }
                    }}
                    onTouchStart={(e) => {
                      isTouchingRef.current = true;
                      if (e.cancelable) e.preventDefault();
                      mobilePreviewAnchorRectRef.current = e.currentTarget.getBoundingClientRect();
                      // If popup open, close immediately and suppress next click
                      if (contextMenu.open) {
                        setContextMenu({ open: false, x: 0, y: 0, app: null });
                        suppressNextClickRef.current = true;
                      }
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                      const touch = e.touches && e.touches[0];
                      const timer = setTimeout(() => {
                        const t = touch || (e.touches && e.touches[0]);
                        const x = t ? t.clientX : 0;
                        const y = t ? t.clientY : 0;
                        setContextMenu({ open: true, x, y, app });
                      }, MOBILE_CONTEXT_MENU_DELAY_MS);
                      setLongPressTimer(timer);
                    }}
                    onClick={(e) => {
                      // If previous down/touch closed popup, skip this navigation
                      if (suppressNextClickRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                        suppressNextClickRef.current = false;
                        return;
                      }
                      if (contextMenu.open) {
                        e.preventDefault();
                        setContextMenu({ open: false, x: 0, y: 0, app: null });
                        return;
                      }
                      const token = localStorage.getItem('appstar_jwt');
                      if (!token) {
                        navigate('/login');
                        return;
                      }
                      axios
                        .post(serverDomain + 'is_user_logged_in.php', null, { params: { token } })
                        .then((response) => {
                          if (response.data && response.data.success === 1) {
                            localStorage.setItem('appstar_app_name', app.app_name);
                            localStorage.setItem('appstar_app_id', app.app_id);
                            navigate('/run');
                          } else {
                            localStorage.setItem('appstar_jwt', '');
                            localStorage.setItem('appstar_admin', 'false');
                            navigate('/login');
                          }
                        })
                        .catch(() => {
                          localStorage.setItem('appstar_admin', 'false');
                          navigate('/login');
                        });
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({ open: true, x: e.clientX, y: e.clientY, app });
                    }}
                    onTouchEnd={() => {
                      isTouchingRef.current = false;
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                  >
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '12px',
                      background: hasIconData ? 'transparent' : (iconStyle.background || '#ccc'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px',
                      fontWeight: 'bold',
                      color: iconStyle.textColor || '#000',
                      boxShadow: isSelected ? '0 0 0 4px #9e9e9e, 0 2px 8px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.15)',
                      border: isSelected ? `2px solid ${pageBg}` : 'none',
                      animation: isSelected ? 'pulseRing 1.6s ease-in-out infinite' : 'none',
                      transition: 'transform 0.2s',
                      outline: 'none',
                      outlineOffset: 0,
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      msUserSelect: 'none',
                      MozUserSelect: 'none',
                      WebkitTouchCallout: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      {app.icon_data ? (
                        <img
                          src={app.icon_data}
                          alt=""
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '12px',
                            display: 'block'
                          }}
                        />
                      ) : (
                        firstLetter
                      )}
                    </div>
                    <div style={{
                      marginTop: '8px',
                      fontSize: '10px',
                      textAlign: 'center',
                      width: '110px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {displayName}
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                marginTop: 'auto',
                paddingTop: 32,
                textAlign: 'center',
                fontSize: '0.85rem',
                opacity: 0.7
              }}
            >
              <Copyright />
            </div>
          </>
        
      </div>
      {hoverPreview.open && !isDesktopPreviewEnabled && (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            hideHoverPreview();
          }}
          onTouchStart={(e) => {
            if (e.cancelable) e.preventDefault();
            e.stopPropagation();
            hideHoverPreview();
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 1049, background: 'transparent' }}
        />
      )}
      {hoverPreview.open && (
        <div
          onMouseEnter={() => {
            if (!isDesktopPreviewEnabled) return;
            previewHoveredRef.current = true;
            clearHoverPreviewCloseTimer();
          }}
          onMouseLeave={() => {
            if (!isDesktopPreviewEnabled) return;
            previewHoveredRef.current = false;
            scheduleHoverPreviewHide();
          }}
          style={{
            position: 'fixed',
            left: hoverPreview.left,
            top: hoverPreview.top,
            width: HOVER_PREVIEW_WIDTH,
            height: HOVER_PREVIEW_HEIGHT,
            borderRadius: 14,
            overflow: 'hidden',
            background: isDarkMode ? '#101010' : '#ffffff',
            border: '1px solid #9e9e9e',
            boxShadow: '0 14px 34px rgba(0,0,0,0.28)',
            zIndex: 1050,
            pointerEvents: 'auto'
          }}
        >
          <div
            style={{
              height: HOVER_PREVIEW_HEADER_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              padding: '0 12px',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: isDarkMode ? '#f5f5f5' : '#1f2937',
              background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              borderBottom: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <div
              title={hoverPreview.appName}
              style={{
                minWidth: 0,
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {hoverPreview.appName}
            </div>
          </div>
          <div
            style={{
              width: '100%',
              height: `calc(100% - ${HOVER_PREVIEW_HEADER_HEIGHT}px)`,
              overflow: 'hidden',
              background: '#ffffff'
            }}
          >
            <iframe
              srcDoc={
                hoverPreview.isLoading
                  ? BLANK_DOC
                  : (hoverPreview.htmlSource.trim() ? hoverPreview.htmlSource : NO_CONTENT_DOC)
              }
              title={`${hoverPreview.appName} preview`}
              style={{
                width: `${HOVER_PREVIEW_VIEWPORT_WIDTH}px`,
                height: `${HOVER_PREVIEW_VIEWPORT_HEIGHT}px`,
                border: 'none',
                background: '#ffffff',
                transform: `scale(${HOVER_PREVIEW_SCALE})`,
                transformOrigin: 'top left'
              }}
              sandbox="allow-scripts allow-modals allow-same-origin"
              tabIndex={-1}
            />
          </div>
        </div>
      )}
      {/* Intercept outside clicks/touches when menu is open to avoid triggering icons */}
      {contextMenu.open && (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            suppressNextClickRef.current = true;
            setContextMenu({ open: false, x: 0, y: 0, app: null });
            // Auto-clear suppression after this outside interaction
            setTimeout(() => { suppressNextClickRef.current = false; }, 0);
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchEnd={(e) => {
            if (e.cancelable) e.preventDefault();
            e.stopPropagation();
            suppressNextClickRef.current = true;
            setContextMenu({ open: false, x: 0, y: 0, app: null });
            setTimeout(() => { suppressNextClickRef.current = false; }, 0);
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 1199, background: 'transparent' }}
        />
      )}
      {contextMenu.open && (
        <div
          ref={contextMenuRef}
          style={{
            position: 'fixed',
            left: '50%',
            top: '42%',
            transform: 'translate(-50%, -50%)',
            background: isDarkMode ? '#2b2b2b' : '#ffffff',
            color: pageText,
            border: '1px solid rgba(0,0,0,0.2)',
            borderRadius: 8,
            boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
            zIndex: 1200,
            width: 200
          }}
        >
          <style>{`
            .context-item {
                  width: 100%;
              padding: 10px 12px;
              background: transparent;
              border: none;
                  text-align: left;
              cursor: pointer;
              color: inherit;
                  display: block;
              font-size: 16px;
            }
            .context-item:hover {
              background: #1976d2;
              color: #ffffff;
            }
            .context-item.danger {
              color: #c62828;
            }
          `}</style>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
            <button
              onClick={() => setContextMenu({ open: false, x: 0, y: 0, app: null })}
              style={{
                background: 'transparent',
                border: 'none',
                color: pageText,
                cursor: 'pointer',
                fontSize: 14
              }}
            >X</button>
          </div>
          <div>
            <button
              className="context-item"
              onClick={(e) => {
                e.stopPropagation();
                setContextMenu({ open: false, x: 0, y: 0, app: null });
                if (contextMenu.app) {
                  localStorage.setItem('appstar_app_name', contextMenu.app.app_name || '');
                  localStorage.setItem('appstar_app_id', contextMenu.app.app_id || '');
                }
                // Defer navigation to avoid any capturing handlers interfering
                setTimeout(() => navigate('/chat'), 0);
              }}
            >Edit app in chat</button>
            <button
              className="context-item"
              onClick={(e) => {
                e.stopPropagation();
                setContextMenu({ open: false, x: 0, y: 0, app: null });
                if (contextMenu.app) {
                  localStorage.setItem('appstar_app_name', contextMenu.app.app_name || '');
                  localStorage.setItem('appstar_app_id', contextMenu.app.app_id || '');
                }
                setTimeout(() => navigate('/icon-editor'), 0);
              }}
            >Edit Icon</button>
            <button
              className="context-item"
              onClick={(e) => {
                e.stopPropagation();
                setContextMenu({ open: false, x: 0, y: 0, app: null });
                setRenameDialog({ open: true, name: (contextMenu.app?.app_name) || '', app: contextMenu.app, loading: false });
              }}
            >Rename app</button>
            {!isDesktopPreviewEnabled && (
              <button
                className="context-item"
                onClick={(e) => {
                  e.stopPropagation();
                  const previewApp = contextMenu.app;
                  const previewRect = mobilePreviewAnchorRectRef.current || getFallbackPreviewRect();
                  setContextMenu({ open: false, x: 0, y: 0, app: null });
                  if (previewApp) {
                    suppressNextClickRef.current = true;
                    setTimeout(() => { suppressNextClickRef.current = false; }, 0);
                    loadHoverPreview(previewApp, previewRect, { allowMobile: true });
                  }
                }}
              >Preview app</button>
            )}
            <button
              className="context-item"
              onClick={(e) => {
                e.stopPropagation();
                const appToClone = contextMenu.app;
                setContextMenu({ open: false, x: 0, y: 0, app: null });
                cloneApp(appToClone);
              }}
            >Clone app</button>
            <button
              className="context-item danger"
              onClick={(e) => {
                e.stopPropagation();
                setContextMenu({ open: false, x: 0, y: 0, app: null });
                setDeleteDialog({ open: true, app: contextMenu.app, loading: false });
              }}
            >Delete app</button>
          </div>
        </div>
      )}

      {(renameDialog.open || deleteDialog.open) && (
        <div
          onMouseDown={(e) => {
            // Intercept clicks on the backdrop so icons don't navigate
            e.preventDefault();
            e.stopPropagation();
            if (renameDialog.open && !renameDialog.loading) setRenameDialog({ open: false, name: '', app: null, loading: false });
            if (deleteDialog.open && !deleteDialog.loading) setDeleteDialog({ open: false, app: null, loading: false });
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 1100 }}
        />
      )}

      {renameDialog.open && (
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <div style={{ width: 360, background: isDarkMode ? '#2b2b2b' : '#fff', color: pageText, borderRadius: 10, boxShadow: '0 10px 24px rgba(0,0,0,0.35)', transform: 'translateY(-50px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
              <div style={{ fontWeight: 600 }}>Rename App</div>
              <button
                onClick={() => !renameDialog.loading && setRenameDialog({ open: false, name: '', app: null, loading: false })}
                style={{ background: 'transparent', border: 'none', color: pageText, cursor: 'pointer' }}
              >X</button>
            </div>
            <div style={{ padding: '16px' }}>
              <input
                type="text"
                value={renameDialog.name}
                ref={renameInputRef}
                onChange={(e) => setRenameDialog((s) => ({ ...s, name: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!renameDialog.app || renameDialog.loading) return;
                    const token = localStorage.getItem('appstar_jwt');
                    if (!token) return;
                    setRenameDialog((s) => ({ ...s, loading: true }));
                    console.log('Renaming app to:', renameDialog.name);
                    axios
                      .patch(
                        serverDomain + 'rename-app.php',
                        null,
                        { params: { token, app_id: renameDialog.app.app_id, new_name: renameDialog.name } }
                      )
                      .then(() => {
                        setRenameDialog({ open: false, name: '', app: null, loading: false });
                        refreshAllAppNames();
                        console.log('App renamed successfully');
                      })
                      .catch((err) => {
                        console.error('Rename error:', err);
                        setRenameDialog({ open: false, name: '', app: null, loading: false });
                      });
                  }
                }}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.2)', marginBottom: 12, background: isDarkMode ? '#1f1f1f' : '#fff', color: pageText }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    if (!renameDialog.app) return;
                    const token = localStorage.getItem('appstar_jwt');
                    if (!token) return;
                    setRenameDialog((s) => ({ ...s, loading: true }));
                    console.log('Renaming app to:', renameDialog.name);
                    axios
                      .patch(serverDomain + 'rename-app.php', null, { params: { token, app_id: renameDialog.app.app_id, new_name: renameDialog.name } })
                      .then(() => {
                        setRenameDialog({ open: false, name: '', app: null, loading: false });
                        refreshAllAppNames();
                        console.log('App renamed successfully');
                      })
                      .catch((err) => {
                        console.error('Rename error:', err);
                        setRenameDialog({ open: false, name: '', app: null, loading: false });
                      });
                  }}
                  style={{ flex: 1, padding: '10px 12px', background: headerBg, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  className="dialog-btn primary"
                >Rename</button>
                <button
                  onClick={() => setRenameDialog({ open: false, name: '', app: null, loading: false })}
                  className="dialog-btn neutral"
                  style={{ flex: 1, padding: '10px 12px', background: isDarkMode ? '#424242' : '#e0e0e0', color: pageText, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                >Cancel</button>
              </div>
              {renameDialog.loading && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: 32, height: 32, border: '4px solid rgba(0,0,0,0.1)', borderTop: '4px solid ' + (isDarkMode ? '#ffffff' : '#003366'), borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteDialog.open && (
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <div style={{ width: 360, background: isDarkMode ? '#2b2b2b' : '#fff', color: pageText, borderRadius: 10, boxShadow: '0 10px 24px rgba(0,0,0,0.35)', transform: 'translateY(-50px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
              <div style={{ fontWeight: 600 }}>Delete App</div>
              <button
                onClick={() => !deleteDialog.loading && setDeleteDialog({ open: false, app: null, loading: false })}
                style={{ background: 'transparent', border: 'none', color: pageText, cursor: 'pointer' }}
              >X</button>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ marginBottom: 12 }}>Are you sure you want to delete this app?</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    if (!deleteDialog.app) return;
                    const token = localStorage.getItem('appstar_jwt');
                    if (!token) return;
                    setDeleteDialog((s) => ({ ...s, loading: true }));
                    console.log('Deleting app:', deleteDialog.app.app_id);
                    axios
                      .delete(serverDomain + 'delete-app.php', { params: { token, app_id: deleteDialog.app.app_id } })
                      .then(() => {
                        console.log('App deleted successfully');
                        setDeleteDialog({ open: false, app: null, loading: false });
                        refreshAllAppNames();
                      })
                      .catch((err) => {
                        console.error('Delete error:', err);
                        setDeleteDialog({ open: false, app: null, loading: false });
                      });
                  }}
                  style={{ flex: 1, padding: '10px 12px', background: '#c62828', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  className="dialog-btn danger"
                >Yes, Delete</button>
                <button
                  onClick={() => setDeleteDialog({ open: false, app: null, loading: false })}
                  className="dialog-btn neutral"
                  style={{ flex: 1, padding: '10px 12px', background: isDarkMode ? '#424242' : '#e0e0e0', color: pageText, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                >No, Cancel</button>
              </div>
              {deleteDialog.loading && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: 32, height: 32, border: '4px solid rgba(0,0,0,0.1)', borderTop: '4px solid ' + (isDarkMode ? '#ffffff' : '#003366'), borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: '#333',
          color: '#fff',
          padding: '12px 16px',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          zIndex: 1100,
          fontSize: '0.9rem'
        }}>{toast}</div>
      )}
    </div>
  );
}
